// import { RouteRecordRaw, RouteMeta, RouteComponent } from 'vue-router';
// eslint-disable-next-line
// @ts-nocheck
import { i18n } from '@/lang/i18n';
import { useCommonStore } from '@/store/common';
import { pinia } from '@/store';
import { isPC } from '@/utils/tools';

const { t } = i18n.global;
const commonStore = useCommonStore(pinia);

/** 跳转至 nuxt 项目 */
const forwardToExternal = (to, from, next) => {
  if (import.meta.env.MODE !== 'development') {
    let locale = i18n.global.locale.value || 'en-US';
    if (locale === 'fa-IR') locale = 'fa';
    const path = to.fullPath.replace(/^\/?(fa|[a-z]{2}-[A-Z]{2})\//, '/');
    window.location.href = `/${locale}${path}`;
    next(false);
  } else {
    next();
  }
};

// 不同布局需要的组件
const layoutComponents: Record<string, any> = {
  default: {
    header: () => import('@/components/layout/header/Index.vue'),
    footer: () => import('@/components/layout/components/AsyncFooterNew.vue')
  },
  webNew: {
    header: () => import('@/components/layout/header/Index.vue'),
    footer: () => import('@/components/layout/components/FooterNew.vue')
  },
  exchange: {
    header: () => import('@/components/layout/header/Index.vue')
  },
  login: {
    header: () => import('@/components/layout/components/LoginHeader.vue')
    // footer: () => import('@/components/layout/components/LoginFooter.vue')
  },
  agent: {
    header: () => import('@/components/layout/header/Index.vue')
  },
  h5: {
    header: () => import('@/components/layout/H5HeaderLR.vue')
  },
  // H5的活动兼容app内嵌和H5浏览器
  activityH5: {
    header: () => import('@/components/layout/H5HeaderLR.vue')
  },
  activity: {
    header: () => import('@/components/layout/components/ActiveHeader.vue'),
    footer: () => import('@/components/layout/components/AsyncFooter.vue')
  },
  blank: {}
};

/**
 * 转换布局格式
 * 解决路由加参数无法跳转Bug，以及浏览器前进后退键无法使用问题
 * 本质原因：必须在App.vue文件里写根路由的 router-view 并确保router-view处于最外层
 *
 * @param routes
 */
const routesAddLayout = (routes: RouteRecordRaw[]) => {
  return routes.map((route: RouteRecordRaw) => {
    const meta: RouteMeta = route.meta || {};
    const layout = String(meta.layout || '');
    const components: Record<string, RouteComponent> = layoutComponents[layout];
    const routeComponents = route.components || {};

    // 路由default组件换赋值方式
    if (route.component) {
      routeComponents.default = route.component;
    }

    // 路由布局组件赋值
    if (components) {
      Object.keys(components).forEach(key => {
        routeComponents[key] = components[key];
      });
      delete route.component;
      route.components = routeComponents;
    }
    return route;
  });
};

/**
 * 路由集中处理
 *
 * @param routes 路由配置
 * @returns
 */
const routesHandle = (routes: RouteRecordRaw[]) => {
  routes = routesAddLayout(routes);

  return routes;
};

let routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/pages/home/index.vue'),
    meta: {
      layout: 'webNew',
      theme: 'dark',
      props: { header: { theme: 'dark', border: false }, footer: { margin: false } },
      title: t('Toobit - 加密货币交易平台｜交易比特币、以太坊的加密货币交易所'),
      keywords: t('加密货币交易所 ｜数字货币交易平台 ｜比特币交易 ｜Toobit'),
      description: t('Toobit是一家领先的加密货币交易所，提供比特币、以太坊和各种加密货币的现货、合约和跟单交易服务。'),
      ssg: {
        preload: [commonStore.getSymbols, commonStore.getTokenList]
      },
      mobilePath: 'h5/home',
      pageId: 1,
      im: true
    }
  },
  {
    path: '/trader-detail',
    component: () => import('@/pages/copy-trading/trader-info/trader-detail.vue'),
    meta: {
      title: t('Toobit - 加密货币交易平台｜交易比特币、以太坊的加密货币交易所'),
      keywords: t('加密货币交易所 ｜数字货币交易平台 ｜比特币交易 ｜Toobit'),
      description: t('Toobit是一家领先的加密货币交易所，提供比特币、以太坊和各种加密货币的现货、合约和跟单交易服务。'),
      layout: 'blank'
    }
  },
  {
    path: '/h5/home',
    component: () => import('@/pages/home/h5/Index.vue'),
    meta: {
      props: { header: { theme: 'dark' } },
      class: { layout: 'dark' },
      layout: 'blank',
      title: t('买卖交易比特币、以太币等加密货币的平台')
    }
  },
  {
    path: '/register',
    component: () => import('@/pages/register/Index.vue'),
    meta: {
      layout: 'login',
      pageId: 0,
      title: t('买卖交易比特币、以太币等加密货币的平台'),
      ssg: { contentHidden: true },
      responsive: (path, meta, params, query, hash) => {
        if (!isPC()) {
          return {
            path: path.includes('/fa') ? '/h5/register/persian' : '/register/invite',
            replace: false,
            params,
            query: path.includes('/fa') ? { ...query, origin: 'fa-register' } : query,
            hash
          };
        } else if (path.includes('/fa')) {
          return {
            path: '/register/persian',
            replace: false,
            params,
            query: { ...query, origin: 'fa-register' },
            hash
          };
        }
      }
    }
  },
  {
    path: '/register/telegram',
    component: () => import('@/pages/register/telegram.vue'),
    meta: {
      layout: 'login',
      pageId: 0,
      title: t('买卖交易比特币、以太币等加密货币的平台'),
      telegram: true,
      theme: 'dark'
    }
  },
  {
    path: '/recharge/:currency',
    component: () => import('@/pages/recharge/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('充值')
    }
  },
  {
    path: '/Invite/h5',
    component: () => import('@/pages/user/h5/Invite.vue'),
    meta: {
      layout: 'h5',
      auth: 'ignore',
      theme: 'dark'
    }
  },
  {
    path: '/register/invite',
    component: () => import('@/pages/register/invite/Invite.vue'),
    meta: {
      layout: 'blank',
      title: t('注册'),
      props: { header: { theme: 'dark' } },
      class: { layout: 'dark' },
      h5Register: true,
      ssg: true,
      responsive: (path, meta, params, query, hash) => {
        if (isPC()) {
          return {
            path: path.includes('/fa') ? '/register/persian' : '/register',
            replace: false,
            params,
            query: path.includes('/fa') ? { ...query, origin: 'fa-register' } : query,
            hash
          };
        } else if (path.includes('/fa')) {
          return {
            path: '/h5/register/persian',
            replace: false,
            params,
            query: { ...query, origin: 'fa-register' },
            hash
          };
        }
      }
    }
  },
  {
    path: '/InviteKorea/h5',
    component: () => import('@/pages/register/invite/InviteTurkey.vue'),
    meta: {
      layout: 'h5'
    }
  },
  {
    path: '/recharge',
    component: () => import('@/pages/recharge/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('充值')
    }
  },
  {
    path: '/limit',
    component: () => import('@/pages/limit/Index.vue'),
    meta: {
      // layout: 'default',
      // auth: 'login',
      title: t('买卖交易比特币、以太币等加密货币的平台'),
      class: {
        layout: 'responsive-layout overflow-hidden'
      }
    }
  },
  {
    path: '/address',
    component: () => import('@/pages/withdrawal/Address.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('地址管理')
    }
  },
  {
    path: '/download',
    component: () => import('@/pages/download/Index.vue'),
    meta: {
      layout: 'default',
      title: t('下载'),
      class: {
        layout: 'download-layout'
      },
      mobilePath: 'h5/download'
    }
  },
  {
    path: '/h5/download',
    component: () => import('@/pages/download/h5/Index.vue'),
    meta: {
      props: { header: { theme: 'dark' } },
      class: { layout: 'dark' },
      layout: 'blank',
      title: t('下载'),
      pcPath: 'download'
    }
  },

  {
    // iframe options download 配置有问题 新加了一个 tgApp/download 路由
    path: '/tgApp/download',
    component: () => import('@/pages/download/Index.vue'),
    meta: {
      layout: 'default',
      title: t('下载'),
      class: {
        layout: 'download-layout'
      }
    }
  },
  {
    path: '/withdrawal/:currency',
    component: () => import('@/pages/withdrawal/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('提现')
    }
  },
  {
    path: '/withdrawal',
    component: () => import('@/pages/withdrawal/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('提现')
    }
  },
  {
    path: '/login',
    component: () => import('@/pages/login/Index.vue'),
    meta: {
      layout: 'login',
      pageId: 0,
      title: t('登录')
    }
  },
  {
    path: '/login/telegram',
    component: () => import('@/pages/login/telegram.vue'),
    meta: {
      layout: 'login',
      pageId: 0,
      title: t('Telegram'),
      telegram: true
    }
  },
  {
    path: '/telegram/bind',
    component: () => import('@/pages/telegram/bind.vue'),
    meta: {
      layout: 'login',
      pageId: 0,
      title: t('Telegram'),
      telegram: true
    }
  },
  {
    path: '/h5/login',
    component: () => import('@/pages/login/h5/Index.vue'),
    meta: {
      layout: 'h5',
      theme: 'dark',
      title: t('登录'),
      props: { header: { theme: 'dark' } },
      class: { layout: 'dark' }
    }
  },
  {
    path: '/reset-password',
    component: () => import('@/pages/reset-password/Layout.vue'),
    meta: {
      layout: 'login',
      title: t('忘记密码')
    }
  },
  {
    path: '/spot/:symbolPath',
    component: () => import('@/pages/spot/Index.vue'),
    beforeEnter: (to, from, next) => {
      let locale = i18n.global.locale.value || 'en-US';
      if (locale === 'fa-IR') {
        locale = 'fa';
      }
      if (to.fullPath.startsWith(`/${locale}`)) {
        window.location.href = to.fullPath;
      } else {
        window.location.href = `/${locale}${to.fullPath}`;
      }
      next(false);
    },
    meta: {
      layout: 'exchange',
      theme: true,
      gridlayout: 'spot',
      class: {
        header: 'base-wrapper',
        layout: 'responsive-layout overflow-hidden'
      },
      title: t('Toobit｜现货'),
      keywords: t('加密货币交易所，比特币交易，购买加密货币，BTC价格'),
      description: t('在行业领先的加密货币交易所交易BTC兑USDT和其他加密货币'),
      props: { header: { border: false, headerSetting: true } },
      // ssg: { preload: [commonStore.getSymbols, commonStore.getCurrency] },
      pageId: 3,
      im: true,
      external: true
    }
  },
  {
    path: '/spot',
    component: () => import('@/pages/spot/Index.vue'),
    beforeEnter: (to, from, next) => {
      let locale = i18n.global.locale.value || 'en-US';
      if (locale === 'fa-IR') {
        locale = 'fa';
      }
      if (to.fullPath.startsWith(`/${locale}`)) {
        window.location.href = to.fullPath;
      } else {
        window.location.href = `/${locale}${to.fullPath}`;
      }

      next(false);
    },
    meta: {
      layout: 'exchange',
      theme: true,
      gridlayout: 'spot',
      class: {
        header: 'base-wrapper',
        layout: 'responsive-layout overflow-hidden'
      },
      title: t('Toobit｜现货'),
      keywords: t('加密货币交易所，比特币交易，购买加密货币，BTC价格'),
      description: t('在行业领先的加密货币交易所交易BTC兑USDT和其他加密货币'),
      props: { header: { border: false, headerSetting: true } },
      // ssg: { preload: [commonStore.getSymbols, commonStore.getCurrency] },
      pageId: 3,
      external: true
    }
  },
  {
    path: '/user',
    component: () => import('@/pages/user/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('个人中心')
    },
    redirect: '/user/info',
    children: [
      {
        path: 'info',
        component: () => import('@/pages/user/Info.vue'),
        meta: {
          title: t('账户总览')
        }
      },
      {
        path: 'security',
        component: () => import('@/pages/user/Security.vue'),
        meta: {
          title: t('安全设置')
        }
      },
      {
        path: 'preferences',
        component: () => import('@/pages/user/Preferences.vue'),
        meta: {
          title: t('偏好设置')
        }
      },
      {
        path: 'kyc',
        component: () => import('@/pages/user/Kyc.vue'),
        meta: {
          title: t('身份认证')
        }
      },
      {
        path: 'auth/:level',
        component: () => import('@/pages/user/Auth.vue'),
        meta: {
          title: t('身份认证')
        }
      },
      {
        path: 'kyc-unbind',
        component: () => import('@/pages/user/KycUnbind.vue'),
        meta: {
          title: t('申请解除绑定')
        }
      },
      // {
      //   path: 'api',
      //   component: () => import('@/pages/user/Api.vue'),
      //   meta: {
      //     title: t('API')
      //   }
      // },
      {
        path: 'bind/:type',
        component: () => import('@/pages/user/Bind.vue'),
        meta: {
          title: t('安全设置')
        }
      },
      {
        path: 'change_bind/:type',
        component: () => import('@/pages/user/ChangeBind.vue'),
        meta: {
          title: t('安全设置')
        }
      },
      {
        path: 'resetpassword',
        component: () => import('@/pages/user/ResetPassword.vue'),
        meta: {
          title: t('安全设置')
        }
      },
      {
        path: 'fundpassword',
        component: () => import('@/pages/user/FundPassword.vue'),
        meta: {
          title: t('安全设置')
        }
      },
      {
        path: 'fish-code',
        component: () => import('@/pages/user/FishCode.vue'),
        meta: {
          title: t('安全设置')
        }
      },
      {
        path: 'senior-auth',
        component: () => import('@/pages/user/SeniorAuth.vue'),
        meta: {
          title: t('身份认证')
        }
      },
      {
        path: 'base-auth',
        component: () => import('@/pages/user/BaseAuth.vue'),
        meta: {
          title: t('身份认证')
        }
      },
      {
        path: 'senior-auth/:state',
        component: () => import('@/pages/user/SeniorAuth.vue'),
        meta: {
          title: t('身份认证')
        }
      },
      {
        path: 'invite',
        component: () => import('@/pages/user/invite/Index.vue'),
        meta: {
          theme: 'dark',
          props: { header: { border: false }, footer: { margin: false } },
          title: t('Toobit 邀请返佣'),
          keywords: t('Toobit邀请返佣，邀请计划'),
          description: t('邀请朋友时可获得高达40%的返佣'),
          h5: 'invite'
        }
      },
      {
        path: 'sub-account',
        component: () => import('@/pages/user/SubAccount.vue'),
        meta: {
          title: t('子账户')
        }
      },
      {
        path: 'sub-api',
        component: () => import('@/pages/user/SubApi.vue'),
        meta: {
          title: t('API管理')
        }
      },
      {
        path: 'sub-api/futures-copy/',
        component: () => import('@/pages/user/SubApi.vue'),
        meta: {
          title: t('API管理')
        }
      },
      {
        path: 'account-info',
        component: () => import('@/pages/user/AccountInfo.vue'),
        meta: {
          title: t('账户信息')
        }
      },
      {
        path: 'unbindingGA',
        component: () => import('@/pages/user/UnbindingGA.vue'),
        meta: {
          auth: 'ignore',
          title: t('解除谷歌认证')
        }
      },
      {
        path: 'login-record',
        component: () => import('@/pages/user/loginRecord.vue'),
        meta: {
          auth: 'ignore',
          title: t('登录记录'),
          props: { footer: { margin: false } }
        }
      },
      {
        path: 'device-manager',
        component: () => import('@/pages/user/deviceManager.vue'),
        meta: {
          auth: 'ignore',
          title: t('设备管理'),
          props: { footer: { margin: false } }
        }
      }
    ]
  },
  {
    path: '/order/dex',
    component: () => import('@/pages/dex+/web/order/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('DEX+ 订单')
    }
  },
  {
    path: '/order',
    component: () => import('@/pages/order/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('订单')
    }
  },
  {
    path: '/orders/futures',
    component: () => import('@/pages/futures-order/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('合约订单')
    }
  },
  {
    path: '/orders/futures-copy/',
    component: () => import('@/pages/futures-order/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('我的带单')
    }
  },
  {
    path: '/orders/futures-strategy',
    component: () => import('@/pages/futures-strategy-order/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('交易机器人')
    }
  },
  {
    path: '/assets/overview',
    component: () => import('@/pages/assets/Overview.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('资产总览'),
      pageId: 5,
      im: true
    }
  },
  {
    path: '/assets/spot',
    component: () => import('@/pages/assets/Spot.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('现货账户'),
      im: true
    }
  },
  {
    path: '/assets/dex',
    component: () => import('@/pages/assets/Dex+.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('DEX+ 账户'),
      im: true
    }
  },
  {
    path: '/assets/earn',
    component: () => import('@/pages/assets/Earn.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('理财账户')
    }
  },
  {
    path: '/assets/sub',
    component: () => import('@/pages/assets/Sub.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('子账户资产')
    }
  },
  {
    path: '/assets/future',
    component: () => import('@/pages/assets/Future.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('合约账户'),
      im: true
    }
  },
  {
    path: '/assets/future-strategy',
    component: () => import('@/pages/assets/FutureStrategy.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('合约账户'),
      im: true
    }
  },
  {
    path: '/assets/copytrading',
    component: () => import('@/pages/assets/CopyTrading.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('跟单账户'),
      im: true
    }
  },
  {
    path: '/orders/copytrading',
    component: () => import('@/pages/copytrading-order/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('资金流水')
    }
  },
  {
    path: '/assets/future/analysis',
    component: () => import('@/pages/assets-analysis/FuturesAnalysis.vue'),
    meta: {
      layout: 'agent',
      auth: 'login',
      class: {
        layout: 'bg-base-base2'
      },
      title: t('合约盈亏分析')
    }
  },
  {
    path: '/assets/spot/analysis',
    component: () => import('@/pages/assets-analysis/SpotAnalysis.vue'),
    meta: {
      layout: 'agent',
      auth: 'login',
      class: {
        layout: 'bg-base-base2'
      },
      title: t('现货盈亏分析')
    }
  },

  {
    path: '/assets/overview/analysis',
    component: () => import('@/pages/assets-analysis/OverviewAnalysis.vue'),
    meta: {
      layout: 'agent',
      auth: 'login',
      class: {
        layout: 'bg-base-base2'
      },
      title: t('总账户盈亏分析')
    }
  },
  {
    path: '/assets/future-strategy/analysis',
    component: () => import('@/pages/assets-analysis/FutureStrategyAnalysis.vue'),
    meta: {
      layout: 'agent',
      auth: 'login',
      class: {
        layout: 'bg-base-base2'
      },
      title: t('交易机器人盈亏分析')
    }
  },
  {
    path: '/assets/history',
    component: () => import('@/pages/assets-history/History.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('财务历史记录')
    }
  },
  {
    path: '/earn/history',
    component: () => import('@/pages/earn/History.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('理财历史')
    }
  },
  {
    path: '/assets/exchange',
    component: () => import('@/pages/assets-exchange/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('小额资产兑换')
    }
  },
  {
    path: '/assets/exchange/history',
    component: () => import('@/pages/assets-exchange/History.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('小额资产兑换历史')
    }
  },
  {
    path: '/markets',
    component: () => import('@/pages/markets/Index.vue'),
    meta: {
      theme: 'dark',
      layout: 'default',
      title: t('加密货币市场，实时比特币价格'),
      description: t('加密货币市价｜加密货币市值'),
      pageId: 2,
      external: true
    },
    beforeEnter: forwardToExternal
   
  },
  {
    path: '/about-us',
    component: () => import('@/pages/about-us/Index.vue'),
    meta: {
      layout: 'default',
      props: { header: { theme: 'dark', border: false } },
      title: t('关于我们'),
      ssg: true
    }
  },
  {
    path: '/h5/about-us',
    component: () => import('@/pages/about-us/h5/Index.vue'),
    meta: {
      layout: 'h5',
      auth: 'ignore',
      title: t('关于我们')
    }
  },
  {
    path: '/futures-copy',
    component: () => import('@/pages/futures/Index-copy-trade.vue'),
    meta: {
      layout: 'exchange',
      theme: true,
      gridlayout: 'futures',

      class: {
        header: 'base-wrapper',
        layout: 'responsive-layout overflow-hidden'
      },
      props: { header: { border: false, headerSetting: true } },
      title: t('Toobit｜USDT 永续合约'),
      keywords: t('合约交易，比特币交易，比特币合约，BTC价格'),
      description: t('在Toobit合约进行BTCUSDT永续合约交易'),
      ssg: true,
      pageId: 8,
      name: 'futures-copy',
      auth: 'login'
    }
  },
  {
    path: '/futures-copy/:symbolPath',
    component: () => import('@/pages/futures/Index-copy-trade.vue'),
    meta: {
      layout: 'exchange',
      theme: true,
      gridlayout: 'futures',
      class: {
        header: 'base-wrapper',
        layout: 'responsive-layout overflow-hidden'
      },
      props: { header: { border: false, headerSetting: true } },
      title: t('Toobit｜USDT 永续合约'),
      keywords: t('合约交易，比特币交易，比特币合约，BTC价格'),
      description: t('在Toobit合约进行BTCUSDT永续合约交易'),
      ssg: true,
      pageId: 8,
      im: true,
      name: 'futures-copy',
      auth: 'login'
    }
  },
  {
    path: '/futures',
    component: () => import('@/pages/futures/Index.vue'),
    meta: {
      layout: 'exchange',
      theme: true,
      gridlayout: 'futures',
      class: {
        header: 'base-wrapper',
        layout: 'responsive-layout overflow-hidden'
      },
      props: { header: { border: false, headerSetting: true } },
      title: t('Toobit｜USDT 永续合约'),
      keywords: t('合约交易，比特币交易，比特币合约，BTC价格'),
      description: t('在Toobit合约进行BTCUSDT永续合约交易'),
      ssg: true,
      pageId: 4,
      name: 'futures'
    }
  },
  {
    path: '/futures/:symbolPath',
    component: () => import('@/pages/futures/Index.vue'),
    meta: {
      layout: 'exchange',
      theme: true,
      gridlayout: 'futures',
      class: {
        header: 'base-wrapper',
        layout: 'responsive-layout overflow-hidden'
      },
      props: { header: { border: false, headerSetting: true } },
      title: t('Toobit｜USDT 永续合约'),
      keywords: t('合约交易，比特币交易，比特币合约，BTC价格'),
      description: t('在Toobit合约进行BTCUSDT永续合约交易'),
      ssg: true,
      pageId: 4,
      im: true,
      name: 'futures'
    }
  },

  {
    path: '/h5/futures',
    component: () => import('@/pages/futures/H5.vue'),
    meta: {
      layout: 'h5',
      title: 'USDT Perpetual Contracts',
      keywords: 'futures tradinge, Bitcoin Trading, Bitcoin Futures,BTC price',
      description: 'Trade BTCUSDT perpetual contracts on Toobit Futures',
      class: { layout: 'dark' }
    }
  },
  {
    path: '/h5/calculator',
    component: () => import('@/pages/futures/h5/Calculator.vue'),
    meta: {
      layout: '',
      auth: 'ignore',
      title: t('合约计算器')
      // class: { layout: 'dark' }
    }
  },
  {
    path: '/vip',
    component: () => import('@/pages/vip/Index.vue'),
    meta: {
      layout: 'default',
      title: t('费率等级'),
      props: { header: { theme: 'dark' }, footer: { margin: false } },
      class: { layout: 'dark' },
      mobilePath: 'h5/vip'
    }
  },
  {
    path: '/h5/vip',
    component: () => import('@/pages/vip/Index.vue'),
    meta: {
      layout: 'activityH5',
      title: t('费率等级'),
      theme: 'dark',
      pcPath: 'vip'
    }
  },
  {
    path: '/futures/info',
    component: () => import('@/pages/futures-info/Index.vue'),
    meta: {
      layout: 'default',
      title: t('合约信息')
    }
  },
  {
    path: '/futures/rule',
    component: () => import('@/pages/futures-rule/Index.vue'),
    meta: {
      layout: 'default',
      title: t('合约规则')
    }
  },
  {
    path: '/token/status',
    component: () => import('@/pages/token-info/Index.vue'),
    meta: {
      layout: 'default',
      title: t('币种状态')
    }
  },
  // {
  //   path: '/test/depth',
  //   component: () => import('@/pages/test/Depth.vue'),
  //   meta: {
  //     layout: 'default',
  //     title: t('测试')
  //   }
  // },
  {
    path: '/futures-activity',
    component: () => import('@/pages/futures-activity/Index.vue'),
    meta: {
      layout: 'default',
      title: t('合约邀新活动')
      // props: { header: { border: false } },
      // class: { layout: 'dark' }
    }
  },
  {
    path: '/h5/futures-activity',
    component: () => import('@/pages/futures-activity/h5/Index.vue'),
    meta: {
      layout: 'h5',
      auth: 'ignore',
      title: t('合约邀新活动')
    }
  },
  {
    path: '/404',
    component: () => import('@/pages/404.vue'),
    meta: {
      layout: 'default',
      class: {
        footer: 'hidden',
        layout: 'responsive-layout overflow-hidden'
      }
    }
  },
  {
    path: '/price/:symbolPath',
    component: () => import('@/pages/price/Index.vue'),
    meta: {
      title: t('数字资产信息｜区块和白皮书｜Toobit'),
      layout: 'default',
      ssg: {
        preload: [commonStore.getSymbols, commonStore.getTokenList]
      }
    }
  },
  {
    path: '/price',
    component: () => import('@/pages/price/Index.vue'),
    meta: {
      title: t('数字资产信息｜区块和白皮书｜Toobit'),
      layout: 'default',
      ssg: true
    }
  },
  {
    path: '/halving',
    component: () => import('@/pages/halving/Index.vue'),
    meta: {
      title: t('比特币减半倒计时｜下一次比特币减半是什么时候？｜Toobit'),
      description: t('比特币减半是每四年发生一次的事件，比特币挖矿的奖励减半。下一次比特币减半发生在2024年4月。'),
      layout: 'default',
      theme: 'dark',
      class: {
        layout: 'halving-box',
        header: 'halving-header',
        footer: 'halving-footer'
      },
      props: { header: { border: false } },
      ssg: true
    }
  },
  {
    path: '/affiliates/',
    component: () => import('@/pages/affiliates/Index.vue'),
    meta: {
      layout: 'agent',
      class: { layout: 'layout-affiliates-info' },
      props: { header: { theme: 'dark', border: false }, footer: { margin: false } },
      auth: 'login',
      title: t('代理人'),
      mobilePath: 'h5/affiliates/info'
    },
    redirect: '/affiliates/info',
    children: [
      {
        path: 'info',
        component: () => import('@/pages/affiliates/Info.vue'),
        meta: {
          auth: 'login',
          title: t('概括')
        }
      },
      {
        path: 'details',
        component: () => import('@/pages/affiliates/PostDetails.vue'),
        meta: {
          auth: 'login',
          title: t('代理人公告')
        }
      },
      {
        path: 'invitation-link',
        redirect: '/affiliates/invitation/link'
      },
      {
        path: 'invitation',
        component: () => import('@/pages/affiliates/Invitation.vue'),
        meta: {
          auth: 'login',
          title: t('链接管理')
        },
        redirect: '/affiliates/invitation/link',
        children: [
          {
            path: 'link',
            component: () => import('@/pages/affiliates/InvitationLink.vue'),
            meta: {
              auth: 'login',
              title: t('邀请链接')
            }
          },
          {
            path: 'data',
            component: () => import('@/pages/affiliates/InvitationData.vue'),
            meta: {
              auth: 'login',
              title: t('邀请码数据')
            }
          }
        ]
      },
      {
        path: 'partner',
        component: () => import('@/pages/affiliates/Partner.vue'),
        meta: {
          auth: 'login',
          title: t('子代理人')
        }
      },
      {
        path: 'partner-bd',
        component: () => import('@/pages/affiliates/components/PartnerBD.vue'),
        meta: {
          auth: 'login',
          title: t('子代理人')
        }
      },
      {
        path: 'commission',
        component: () => import('@/pages/affiliates/Commission.vue'),
        meta: {
          auth: 'login',
          title: t('分佣收入')
        }
      },
      {
        path: 'my-customers',
        component: () => import('@/pages/affiliates/MyCustomers.vue'),
        meta: {
          auth: 'login',
          title: t('用户管理')
        },
        redirect: '/affiliates/my-customers/list',
        children: [
          {
            path: 'list',
            component: () => import('@/pages/affiliates/MyCustomersList.vue'),
            meta: {
              auth: 'login',
              title: t('直客列表')
            }
          },
          {
            path: 'reward',
            component: () => import('@/pages/affiliates/MyCustomersReward.vue'),
            meta: {
              auth: 'login',
              title: t('用户福利管理')
            }
          },
          {
            path: 'funds',
            component: () => import('@/pages/affiliates/components/MyCustomersFundsPage.vue'),
            meta: {
              auth: 'login',
              title: t('出入金管理')
            }
          },
          {
            path: 'alarm',
            component: () => import('@/pages/affiliates/components/MyCustomersAlarmPage.vue'),
            meta: {
              auth: 'login',
              title: t('异常报警')
            }
          }
        ]
      },
      {
        path: 'transaction',
        component: () => import('@/pages/affiliates/Transaction.vue'),
        meta: {
          auth: 'login',
          title: t('交易管理')
        }
      },
      {
        path: 'trade',
        component: () => import('@/pages/affiliates/Trade.vue'),
        meta: {
          auth: 'login',
          title: t('交易管理')
        },
        redirect: '/affiliates/trade/transaction',
        children: [
          {
            path: 'transaction',
            component: () => import('@/pages/affiliates/Transaction.vue'),
            meta: {
              auth: 'login',
              title: t('合约持仓')
            }
          },
          {
            path: 'history',
            component: () => import('@/pages/affiliates/History.vue'),
            meta: {
              auth: 'login',
              title: t('合约订单')
            }
          },
          {
            path: 'spot-history',
            component: () => import('@/pages/affiliates/SpotHistory.vue'),
            meta: {
              auth: 'login',
              title: t('现货订单')
            }
          }
        ]
      },
      {
        path: 'data/',
        component: () => import('@/pages/affiliates/Data.vue'),
        meta: {
          auth: 'login',
          title: t('数据表现')
        },
        redirect: '/affiliates/data/partner',
        children: [
          {
            path: 'partner',
            component: () => import('@/pages/affiliates/DataPartner.vue'),
            meta: {
              auth: 'login',
              title: t('代理返佣明细')
            }
          },
          {
            path: 'activity',
            component: () => import('@/pages/affiliates/DataActivity.vue'),
            meta: {
              auth: 'login',
              title: t('活动')
            }
          }
        ]
      },
      {
        path: 'activity',
        component: () => import('@/pages/affiliates/Activity.vue'),
        meta: {
          auth: 'login',
          title: t('市场活动')
        }
      },
      {
        path: 'activity-details',
        component: () => import('@/pages/affiliates/Details.vue'),
        meta: {
          auth: 'login',
          title: t('市场活动')
        }
      }
    ]
  },
  {
    path: '/h5/affiliates',
    component: () => import('@/pages/affiliates/h5/Index.vue'),
    redirect: '/h5/affiliates/info',
    meta: {
      layout: 'activityH5',
      title: t('代理人'),
      theme: 'light',
      // props: { header: { theme: 'light' } },
      // auth: 'login',
      pcPath: 'affiliates'
    },
    children: [
      {
        path: 'info',
        component: () => import('@/pages/affiliates/h5/Info.vue'),
        meta: {
          title: t('概括')
        }
      },
      {
        path: 'overview',
        component: () => import('@/pages/affiliates/h5/InfoOverview.vue'),
        meta: {
          title: t('概括')
        }
      },
      {
        path: 'invitation',
        component: () => import('@/pages/affiliates/h5/PageSubView.vue'),
        meta: {
          title: t('链接管理')
        },
        redirect: '/h5/affiliates/invitation/link',
        children: [
          {
            path: 'link',
            component: () => import('@/pages/affiliates/h5/InvitationLink.vue'),
            meta: {
              title: t('邀请链接')
            }
          },
          {
            path: 'data',
            component: () => import('@/pages/affiliates/h5/InvitationData.vue'),
            meta: {
              title: t('邀请码数据')
            }
          },
          {
            path: 'set',
            component: () => import('@/pages/affiliates/h5/InvitationLinkSet.vue'),
            meta: {
              title: t('管理链接')
            }
          },
          {
            path: 'link-bd',
            component: () => import('@/pages/affiliates/h5/InvitationLink.vue'),
            meta: {
              title: t('邀请链接')
            }
          }
        ]
      },
      {
        path: 'partner',
        component: () => import('@/pages/affiliates/h5/Partner.vue'),
        meta: {
          title: t('子代理人')
        }
      },
      {
        path: 'partner-bd',
        component: () => import('@/pages/affiliates/h5/Partner.vue'),
        meta: {
          title: t('子代理人')
        }
      },
      {
        path: 'partner-set',
        component: () => import('@/pages/affiliates/h5/PartnerSet.vue'),
        meta: {
          title: t('子代理人')
        }
      },
      {
        path: 'my-customers',
        component: () => import('@/pages/affiliates/h5/PageSubView.vue'),
        meta: {
          title: t('用户管理')
        },
        redirect: '/h5/affiliates/my-customers/list',
        children: [
          {
            path: 'list',
            component: () => import('@/pages/affiliates/h5/MyCustomersList.vue'),
            meta: {
              title: t('直客列表')
            }
          },
          {
            path: 'reward',
            component: () => import('@/pages/affiliates/h5/MyCustomersReward.vue'),
            meta: {
              title: t('用户福利管理')
            }
          },
          {
            path: 'funds',
            component: () => import('@/pages/affiliates/h5/MyCustomersFunds.vue'),
            meta: {
              title: t('出入金管理')
            }
          },
          {
            path: 'alarm',
            component: () => import('@/pages/affiliates/h5/MyCustomersAlarm.vue'),
            meta: {
              title: t('异常报警')
            }
          }
        ]
      },
      {
        path: 'trade',
        component: () => import('@/pages/affiliates/h5/PageSubView.vue'),
        meta: {
          title: t('交易管理')
        },
        redirect: '/h5/affiliates/trade/contract-current',
        children: [
          {
            path: 'contract-current',
            component: () => import('@/pages/affiliates/h5/ContractCurrent.vue'),
            meta: {
              title: t('合约持仓')
            }
          },
          {
            path: 'contract-history',
            component: () => import('@/pages/affiliates/h5/ContractHistory.vue'),
            meta: {
              title: t('合约订单')
            }
          },
          {
            path: 'spot-history',
            component: () => import('@/pages/affiliates/h5/SpotHistory.vue'),
            meta: {
              title: t('现货订单')
            }
          }
        ]
      },
      {
        path: 'commission',
        component: () => import('@/pages/affiliates/h5/PageSubView.vue'),
        meta: {
          title: t('收入')
        },
        redirect: '/h5/affiliates/commission/info',
        children: [
          {
            path: 'info',
            component: () => import('@/pages/affiliates/h5/Commission.vue'),
            meta: {
              title: t('分佣收入')
            }
          },
          {
            path: 'partner',
            component: () => import('@/pages/affiliates/h5/DataPartner.vue'),
            meta: {
              title: t('子代理返佣')
            }
          }
        ]
      },
      {
        path: 'activity-detail',
        component: () => import('@/pages/affiliates/h5/ActivityDetail.vue'),
        meta: {
          title: t('市场活动')
        }
      },
      {
        path: 'announcement',
        component: () => import('@/pages/affiliates/h5/Announcement.vue'),
        meta: {
          title: t('代理人公告')
        }
      }
    ]
  },
  {
    path: '/t/:path',
    component: () => import('@/pages/affiliates/ShortLink.vue')
  },
  {
    path: '/affiliates/landing',
    component: () => import('@/pages/affiliates/landing/Index.vue'),
    meta: {
      title: t('代理人计划'),
      description: t('邀请新用户，轻松享受实时返佣！'),
      layout: 'default',
      theme: 'dark',
      props: { header: { theme: 'dark', border: false }, footer: { theme: 'dark', margin: false } },
      mobilePath: 'h5/affiliates/landing'
      // ssg: true
    }
  },
  {
    path: '/h5/affiliates/landing',
    component: () => import('@/pages/affiliates/landing/IndexH5.vue'),
    meta: {
      layout: 'activityH5',
      title: t('代理人计划'),
      theme: 'dark',
      pcPath: 'affiliates/landing'
    }
  },
  {
    path: '/affiliates/apply',
    component: () => import('@/pages/affiliates/apply/Index.vue'),
    meta: {
      title: t('代理人申请'),
      layout: 'blank',
      auth: 'login',
      props: { header: { visible: 'right', border: false } },
      mobilePath: 'h5/affiliates/apply'
    }
  },
  {
    path: '/h5/affiliates/apply',
    component: () => import('@/pages/affiliates/apply/h5/Index.vue'),
    meta: {
      layout: 'activityH5',
      title: t('代理人申请'),
      pcPath: 'affiliates/apply'
    }
  },
  {
    path: '/affiliates/recruit',
    component: () => import('@/pages/affiliates/landing/Index.vue'),
    meta: {
      layout: 'default',
      title: t('代理人申请'),
      theme: 'dark',
      props: { header: { theme: 'dark', border: false }, footer: { theme: 'dark', margin: false } },
      mobilePath: 'h5/affiliates/landing'
    }
  },
  {
    path: '/h5/affiliates/recruit',
    component: () => import('@/pages/affiliates/landing/IndexH5.vue'),
    meta: {
      layout: 'activityH5',
      title: t('代理人申请'),
      theme: 'dark',
      pcPath: 'affiliates/landing'
    }
  },
  {
    path: '/btc-activities',
    component: () => import('@/pages/btc-activities/Index.vue'),
    meta: {
      layout: 'default',
      title: t('BTC开仓活动'),
      props: { header: { theme: 'dark', border: false }, footer: { theme: 'dark', margin: false } }
    }
  },
  {
    path: '/h5/btc-activities',
    component: () => import('@/pages/btc-activities/h5/Index.vue'),
    meta: {
      layout: 'ignore',
      title: t('BTC开仓活动'),
      props: { header: { theme: 'dark', border: false }, footer: { theme: 'dark', margin: false } }
    }
  },
  {
    path: '/affiliates/exclusive-activities',
    component: () => import('@/pages/affiliates/ExclusiveActivities/Index.vue'),
    meta: {
      layout: 'default',
      title: t('专属活动'),
      theme: 'dark',
      backPath: ['/login', '/register'],
      class: {
        header: 'base-wrapper',
        footer: 'ea-footer new-color-footer',
        layout: 'base-wrapper'
      },
      props: {
        header: { theme: 'dark' },
        footer: { theme: 'dark', margin: false }
      }
    }
  },
  {
    path: '/h5/affiliates/exclusive-activities',
    component: () => import('@/pages/affiliates/ExclusiveActivities/h5/Index.vue'),
    meta: {
      layout: 'blank',
      title: t('专属活动'),
      theme: 'dark',
      backPath: ['/login', '/register']
    }
  },
  {
    path: '/register/active',
    component: () => import('@/pages/affiliates/register/Index.vue'),
    meta: {
      layout: 'activity',
      title: t('代理人注册'),
      props: { footer: { margin: false } }
    }
  },
  {
    path: '/register/persian',
    component: () => import('@/pages/affiliates/persian-register/web/Index.vue'),
    meta: {
      layout: 'default',
      title: 'ثبت نام در صرافی Toobit رایگان و سریع',
      theme: 'dark',
      h5Register: true,
      class: {
        layout: 'persian-register-layout'
      },
      props: { header: { theme: 'dark' }, footer: { theme: 'dark', margin: false } },
      responsive: (path, meta, params, query, hash) => {
        if (!isPC()) {
          return {
            path: '/h5/register/persian',
            replace: false,
            params,
            query,
            hash
          };
        } else {
          // 如果不是fa并且不带邀请码的情况下跳到普通注册页，因为代理人分享的链接不带语言但是有inviteCode（这种情况下不应该跳走）
          if (!path.includes('/fa') && !query?.invite_code) {
            return {
              path: '/register',
              replace: false,
              params,
              query,
              hash
            };
          }
        }
      }
    }
  },
  {
    path: '/activity/persianwelcome',
    component: () => import('@/pages/activity/persian-welcome/web/Index.vue'),
    meta: {
      layout: 'default',
      title: 'به Toobit خوش آمدید',
      theme: 'dark',
      // auth: 'ignore',
      class: {
        layout: 'persian-register-layout'
      },
      props: { header: { theme: 'dark' } }
    }
  },
  {
    path: '/h5/register/persian',
    component: () => import('@/pages/affiliates/persian-register/h5/Index.vue'),
    meta: {
      layout: 'blank',
      theme: 'dark',
      auth: 'ignore',
      title: 'ثبت نام در صرافی Toobit رایگان و سریع',
      h5Register: true, // 注册页面 自动跳转H5注册的限制，h5Register 不跳转
      class: {
        layout: 'persian-register-layout'
      },
      responsive: (path, meta, params, query, hash) => {
        if (isPC()) {
          return {
            path: '/register/persian',
            replace: false,
            params,
            query,
            hash
          };
        } else {
          // 如果不是fa并且不带邀请码的情况下跳到普通注册页，因为代理人分享的链接不带语言但是有inviteCode（这种情况下不应该跳走）
          if (!path.includes('/fa') && !query?.invite_code) {
            return {
              path: '/register/invite',
              replace: false,
              params,
              query,
              hash
            };
          }
        }
      }
    }
  },
  {
    path: '/h5/activity/persianwelcome',
    component: () => import('@/pages/activity/persian-welcome/h5/Index.vue'),
    meta: {
      layout: 'h5',
      title: 'به Toobit خوش آمدید',
      theme: 'dark',
      class: {
        layout: 'persian-register-layout'
      },
      props: { header: { theme: 'dark' } }
    }
  },
  {
    path: '/copytrading',
    component: () => import('@/pages/copy-trading/trader-list/Index.vue'),
    meta: {
      title: t('Toobit｜跟单交易'),
      keywords: t('跟单交易，加密货币跟单交易，数字货币跟单交易'),
      description: t('在toobit.com发现并跟随顶级交易员'),
      class: {
        layout: 'header-transparent'
      },
      im: true,
      layout: 'default',
      props: { header: { border: false } },
      ssg: true,
      pageId: 7
    }
  },
  {
    path: '/copytrading/ranking',
    component: () => import('@/pages/copy-trading/ranking/Index.vue'),
    meta: {
      title: t('Toobit｜跟单交易排行榜'),
      keywords: t('跟单交易，排行榜'),
      description: t('跟随全球顶级交易员操作'),
      pageId: 7,
      class: {
        layout: 'header-transparent'
      },
      layout: 'default',
      props: { header: { border: false } },
      ssg: true
    }
  },
  {
    path: '/halloween',
    component: () => import('@/pages/activity/Halloween.vue'),
    meta: {
      title: t('万圣节'),
      theme: 'dark',
      class: {
        layout: 'header-transparent'
      },
      layout: 'default',
      props: { header: { theme: 'dark', border: false } }
    }
  },
  {
    path: '/activity/halloween',
    component: () => import('@/pages/activity/Halloween/web/Index.vue'),
    meta: {
      title: t('万圣节'),
      theme: 'dark',
      isStay: true,
      backPath: ['/login', '/register'],
      class: {
        layout: 'activity-halloween-layout',
        header: 'activity-halloween-header'
      },
      layout: 'default',
      props: { header: { theme: 'dark', border: false } }
    }
  },
  {
    path: '/activity/winter',
    component: () => import('@/pages/activity/WinterSolsticeFestival/web/Index.vue'),
    meta: {
      title: 'یلدا با توبیت',
      theme: 'dark',
      isStay: true,
      backPath: ['/login', '/register'],
      class: {
        layout: 'activity-halloween-layout',
        header: 'activity-halloween-header'
      },
      layout: 'default',
      props: { header: { theme: 'dark', border: false } }
    }
  },
  {
    path: '/h5/activity/winter',
    component: () => import('@/pages/activity/WinterSolsticeFestival/h5/Index.vue'),
    meta: {
      title: 'یلدا با توبیت',
      theme: 'dark',
      isStay: true,
      layout: 'h5',
      auth: 'ignore'
    }
  },
  {
    path: '/h5/activity/halloween',
    component: () => import('@/pages/activity/Halloween/h5/Index.vue'),
    meta: {
      title: t('万圣节'),
      theme: 'dark',
      layout: 'h5',
      auth: 'ignore'
    }
  },
  {
    path: '/h5/halloween',
    component: () => import('@/pages/activity/h5/Halloween.vue'),
    meta: {
      title: t('万圣节'),
      theme: 'dark',
      layout: 'h5',
      auth: 'ignore'
    }
  },
  {
    path: '/copytrading/trader/info',
    component: () => import('@/pages/copy-trading/trader-info/Index.vue'),
    meta: {
      title: t('交易员信息'),
      keywords: 'Copy Trading, Crypto Copy Trade,Crypto Copy Trading,',
      description: 'Discover Top Pro Traders to Follow & Copy On toobit.com',
      im: true,
      class: {
        layout: 'header-transparent'
      },
      layout: 'default',
      props: { header: { border: false } }
    }
  },
  {
    path: '/copytrading/trader/Settings',
    component: () => import('@/pages/copy-trading/trader-info/Settings.vue'),
    meta: {
      title: t('交易员设置'),
      keywords: 'Copy Trading, Crypto Copy Trade,Crypto Copy Trading,',
      description: 'Discover Top Pro Traders to Follow & Copy On toobit.com',
      class: {
        layout: 'header-transparent'
      },
      layout: 'default',
      props: { header: { border: false } }
    }
  },
  {
    path: '/copytrading/follower/info',
    component: () => import('@/pages/copy-trading/follower-info/Index.vue'),
    meta: {
      title: t('我的跟单'),
      keywords: 'Copy Trading, Crypto Copy Trade,Crypto Copy Trading,',
      description: 'Discover Top Pro Traders to Follow & Copy On toobit.com',
      class: {
        layout: 'header-transparent'
      },
      layout: 'default',
      auth: 'login',
      im: true,
      props: { header: { border: false } }
    }
  },
  {
    path: '/h5/copytrading/landing',
    component: () => import('@/pages/copy-trading/h5/Landing.vue'),
    meta: {
      title: t('成为Toobit Pro交易员，跟单交易获得最大收益'),
      description: t('成为Toobit上的专业交易者，您可以提高您的平台曝光度，并与跟单用户分享您的最佳交易策略以获取利润。'),
      layout: 'blank',
      auth: 'ignore',
      theme: 'dark'
      // ssg: true
    }
  },
  {
    path: '/copytrading/landing',
    component: () => import('@/pages/copy-trading/landing-trading/Index.vue'),
    meta: {
      title: t('成为Toobit Pro交易员，跟单交易获得最大收益'),
      description: t('成为Toobit上的专业交易者，您可以提高您的平台曝光度，并与跟单用户分享您的最佳交易策略以获取利润。'),
      layout: 'default',
      class: {
        layout: 'header-transparent'
      },
      theme: 'dark',
      props: { header: { theme: 'dark', border: false }, footer: { margin: false } },
      ssg: true
    }
  },
  {
    path: '/h5/dex+',
    component: () => import('@/pages/dex+/h5/Landing.vue'),
    meta: {
      title: t('Web版DEX +交易即将开放'),
      description: t('Web版DEX +交易即将开放'),
      layout: 'h5',
      auth: 'ignore',
      theme: 'dark',
      pcPath: 'dex'
      // ssg: true
    }
  },
  {
    path: '/activity/',
    component: () => import('@/pages/activity/Index.vue'),
    meta: {
      layout: 'default',
      title: t('活动')
    },
    redirect: '/activity/reward-center',
    children: [
      {
        path: 'user-tasks',
        component: () => import('@/pages/activity/NewUserTasks.vue'),
        meta: {
          theme: 'dark',
          class: {
            layout: 'NewUserTasks-box',
            header: 'NewUserTasks-header'
          },
          props: { header: { border: false, theme: 'dark' }, footer: { theme: 'dark', margin: false } },
          title: t('新手礼包'),
          h5: 'activity/user-tasks',
          ssg: true
        }
      },
      {
        path: 'user-tasks/:subPath',
        component: () => import('@/pages/activity/NewUserTasks.vue'),
        meta: {
          theme: 'dark',
          class: {
            layout: 'NewUserTasks-box',
            header: 'NewUserTasks-header'
          },
          props: { header: { border: false, theme: 'dark' } },
          title: t('新手礼包'),
          ssg: true,
          h5: 'activity/user-tasks/:subPath'
        }
      },
      {
        path: 'korea-tasks',
        component: () => import('@/pages/activity/KoreaUserTasks.vue'),
        meta: {
          title: t('新手礼包'),
          class: {
            layout: 'korea-tasks-layout'
          },
          layout: 'default',
          props: { header: { theme: 'dark', border: false }, footer: { theme: 'dark' } }
          // h5: '/h5/activity/korea-tasks'
        }
      },
      {
        path: 'reward-center',
        component: () => import('@/pages/activity/reward-center/Index.vue'),
        meta: {
          class: {
            layout: 'activity-reward-center-bg',
            header: '',
            footer: 'activity-reward-center-bg'
          },
          // theme: 'dark',
          // auth: 'login',
          props: { header: { border: false } },
          title: t('加密货币奖励中心，赚取USDT'),
          description: t('简单任务，丰厚奖励：注册并获得20美元奖金！一站式门户，让您参与任务并获得奖励。')
        }
      },
      {
        path: 'channel',
        component: () => import('@/pages/activity/reward-center/channel/index.vue'),
        meta: {
          class: {
            // layout: 'activity-reward-center-bg',
            header: ''
            // footer: 'activity-reward-center-bg'
          },
          theme: 'dark',
          // auth: 'login',
          props: { header: { border: false }, footer: { margin: false } },
          title: t('活动中心'),
          description: t('活动中心'),
          mobilePath: 'h5/activity/channel'
        },
        beforeEnter: async(to, from, next) => {
          await commonStore.timeCalibration();
          next();
        }
      },
      {
        path: 'reward-complete',
        component: () => import('@/pages/activity/reward-center/Complete.vue'),
        meta: {
          class: {
            layout: ''
          },
          auth: 'login',
          // props: { header: { theme: 'dark', border: false } },
          title: t('福利中心-任务记录')
        }
      },
      {
        path: 'reward-coupon',
        component: () => import('@/pages/activity/reward-center/Coupon.vue'),
        meta: {
          class: {
            layout: ''
          },
          auth: 'login',
          title: t('福利中心-卡券中心')
        }
      }
    ]
  },
  {
    path: '/h5/activity/channel',
    component: () => import('@/pages/activity/reward-center/channel/index.vue'),
    meta: {
      layout: 'blank',
      class: {
        header: ''
      },
      theme: 'dark',
      props: { header: { border: false }, footer: { margin: false } },
      title: t('活动中心'),
      description: t('活动中心'),
      pcPath: 'activity/channel'
    },
    beforeEnter: async(to, from, next) => {
      await commonStore.timeCalibration();
      next();
    }
  },
  // {
  //   path: '/activity/christmas',
  //   component: () => import('@/pages/activity/Christmas/Index.vue'),
  //   meta: {
  //     layout: 'exchange',
  //     class: {
  //       layout: 'activity-christmas-layout',
  //       header: 'activity-christmas-header'
  //     },
  //     props: { header: { border: false } },
  //     title: t('Toobit圣诞大狂欢')
  //   }
  // },
  {
    path: '/activity/christmas',
    component: () => import('@/pages/activity/christmas-2024/web/Index.vue'),
    meta: {
      layout: 'default',
      class: {
        layout: 'activity-christmas-2024-layout',
        header: 'activity-christmas-2024-header',
        footer: 'activity-christmas-2024-footer'
      },
      theme: 'dark',
      backPath: ['/login', '/register'],
      isStay: true,
      props: { header: { border: false } },
      title: t('Toobit圣诞大狂欢'),
      h5: 'activity/christmas'
    }
  },
  {
    path: '/activity/spring-trading',
    component: () => import('@/pages/activity/spring-trading/Index.vue'),
    meta: {
      layout: 'default',
      class: {
        layout: 'activity-spring-trading-layout',
        header: 'activity-spring-trading-header',
        footer: 'activity-spring-trading-footer'
      },
      backPath: ['/login', '/register'],
      isStay: true,
      props: { header: { theme: 'dark', border: false }, footer: { theme: 'dark' } },
      title: t('春日热潮来袭'),
      h5: 'activity/spring-trading'
    }
  },
  {
    path: '/activity/dragon',
    component: () => import('@/pages/activity/Dragon/Index.vue'),
    meta: {
      layout: 'default',
      class: {
        layout: 'activity-dragon-layout',
        header: 'activity-dragon-header'
      },
      props: { header: { theme: 'dark', border: false }, footer: { theme: 'dark' } },
      title: t('Toobit狂欢迎新春')
    }
  },
  {
    path: '/activity/uefa-euro2024',
    component: () => import('@/pages/activity/uefa-euro2024/Index.vue'),
    meta: {
      layout: 'default',
      isStay: true,
      class: {
        layout: 'activity-uefa-euro2024-layout',
        header: 'activity-uefa-euro2024-header'
      },
      theme: 'dark',
      props: { header: { border: false } },
      title: t('欧洲杯交易盛典')
    }
  },
  {
    path: '/activity/persian',
    component: () => import('@/pages/activity/persian/Index.vue'),
    meta: {
      layout: 'default',
      isStay: true,
      class: {
        layout: 'activity-persian-layout',
        header: 'activity-persian-header'
      },
      theme: 'dark',
      props: { header: { border: false } },
      title: 'Persian activity'
    }
  },
  {
    path: '/activity/trading',
    component: () => import('@/pages/activity/trading/index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'default',
      backPath: ['/login', '/register'],
      props: { header: { border: false }, footer: { margin: false } },
      title: t('加密货币奖励中心，赚取USDT'),
      description: t('简单任务，丰厚奖励：注册并获得20美元奖金！一站式门户，让您参与任务并获得奖励。')
    }
  },
  {
    path: '/h5/activity/trading',
    component: () => import('@/pages/activity/persia-trading/Index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'h5',
      title: t('加密货币奖励中心，赚取USDT'),
      description: t('简单任务，丰厚奖励：注册并获得20美元奖金！一站式门户，让您参与任务并获得奖励。')
    }
  },
  {
    path: '/activity/ratio-show',
    component: () => import('@/pages/activity/korea-pnl/web/Index.vue'),
    meta: {
      layout: 'default',
      isStay: true,
      backPath: ['/login', '/register'],
      class: {
        layout: 'activity-persian-layout',
        header: 'header-transparent'
      },
      theme: 'dark',
      props: { header: { border: false } },
      title: 'PNL RATIO SHOW'
    }
  },
  {
    path: '/activity/persia-ton',
    component: () => import('@/pages/activity/persia-tron/web/Index.vue'),
    meta: {
      layout: 'default',
      isStay: true,
      backPath: ['/login', '/register'],
      class: {
        layout: 'activity-persian-layout'
        // header: 'header-transparent'
      },
      theme: 'dark',
      props: { header: { border: false } },
      title: ''
    }
  },
  {
    path: '/h5/activity/persia-ton',
    component: () => import('@/pages/activity/persia-tron/h5/Index.vue'),
    meta: {
      layout: 'h5',
      theme: 'dark',
      isStay: true,
      title: t('')
    }
  },
  {
    path: '/activity/site-show',
    component: () => import('@/pages/activity/Site-pnl/web/Index.vue'),
    meta: {
      layout: 'default',
      isStay: true,
      backPath: ['/login', '/register'],
      class: {
        layout: 'activity-site-layout'
      },
      theme: 'dark',
      props: { header: { border: false } },
      title: t('9月交易狂欢')
    }
  },
  {
    path: '/h5/activity/site-show',
    component: () => import('@/pages/activity/Site-pnl/h5/Index.vue'),
    meta: {
      layout: 'h5',
      theme: 'dark',
      isStay: true,
      title: t('9月交易狂欢')
    }
  },
  {
    path: '/h5/activity/ratio-show',
    component: () => import('@/pages/activity/korea-pnl/h5/Index.vue'),
    meta: {
      layout: 'h5',
      theme: 'dark',
      isStay: true,
      title: 'PNL RATIO SHOW'
    }
  },
  {
    path: '/activity/squad-showdown-competition',
    component: () => import('@/pages/activity/persian-kol/Index.vue'),
    meta: {
      layout: 'default',
      isStay: true,
      backPath: ['/login', '/register'],
      class: {
        layout: 'activity-persian-layout',
        header: 'header-transparent'
      },
      theme: 'dark',
      props: { header: { border: false } },
      title: 'Persian activity'
    }
  },
  {
    path: '/activity/super-first-anniversary',
    component: () => import('@/pages/activity/super-first-anniversary/Index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'default',
      backPath: ['/login', '/register'],
      props: { header: { border: false }, footer: { margin: false } },
      title: '',
      description: t(''),
      mobilePath: 'h5/activity/super-first-anniversary'
    }
  },
  {
    path: '/h5/activity/super-first-anniversary',
    component: () => import('@/pages/activity/super-first-anniversary/Index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'activityH5',
      props: { header: { border: false } },
      title: '',
      pcPath: 'activity/super-first-anniversary'
    }
  },
  {
    path: '/activity/GrandArenaRaffle',
    component: () => import('@/pages/activity/super-first-anniversary/GrandArenaRaffle.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'default',
      backPath: ['/login', '/register'],
      props: { header: { border: false }, footer: { margin: false } },
      title: '',
      description: t(''),
      mobilePath: 'h5/activity/GrandArenaRaffle'
    }
  },
  {
    path: '/h5/activity/GrandArenaRaffle',
    component: () => import('@/pages/activity/super-first-anniversary/GrandArenaRaffle.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'activityH5',
      props: { header: { border: false } },
      title: '',
      pcPath: 'activity/GrandArenaRaffle'
    }
  },
  {
    path: '/activity/Trading-Clash',
    component: () => import('@/pages/activity/super-first-anniversary/TradingClash.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'default',
      backPath: ['/login', '/register'],
      props: { header: { border: false }, footer: { margin: false } },
      title: '',
      description: t(''),
      mobilePath: 'h5/activity/Trading-Clash'
    }
  },
  {
    path: '/h5/activity/Trading-Clash',
    component: () => import('@/pages/activity/super-first-anniversary/TradingClash.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'activityH5',
      props: { header: { border: false } },
      title: '',
      pcPath: 'activity/Trading-Clash'
    }
  },
  {
    path: '/activity/nowruz',
    component: () => import('@/pages/activity/persian-new-year/index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'default',
      backPath: ['/login', '/register'],
      props: { header: { border: false }, footer: { margin: false } },
      title: 'نوروز با توبیت',
      description: t(''),
      mobilePath: 'h5/activity/nowruz'
    }
  },
  {
    path: '/h5/activity/nowruz',
    component: () => import('@/pages/activity/persian-new-year/index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'activityH5',
      props: { header: { border: false }, footer: { margin: false } },
      title: 'نوروز با توبیت',
      pcPath: 'activity/nowruz'
    }
  },
  {
    path: '/activity/quantzon-king-squads',
    component: () => import('@/pages/activity/korea-kol/index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'default',
      backPath: ['/login', '/register'],
      props: { header: { border: false }, footer: { margin: false } },
      title: '',
      description: t(''),
      mobilePath: 'h5/activity/quantzon-king-squads'
    }
  },

  {
    path: '/h5/activity/quantzon-king-squads',
    component: () => import('@/pages/activity/korea-kol/index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'activityH5',
      props: { header: { border: false }, footer: { margin: false } },
      title: '',
      pcPath: 'activity/quantzon-king-squads'
    }
  },

  {
    path: '/activity/King-squads-July',
    component: () => import('@/pages/activity/korea-kol/index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'default',
      backPath: ['/login', '/register'],
      props: { header: { border: false }, footer: { margin: false } },
      title: '',
      description: t(''),
      mobilePath: 'h5/activity/King-squads-July'
    }
  },

  {
    path: '/h5/activity/King-squads-July',
    component: () => import('@/pages/activity/korea-kol/index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'activityH5',
      props: { header: { border: false }, footer: { margin: false } },
      title: '',
      pcPath: 'activity/King-squads-July'
    }
  },
  {
    path: '/activity/EidalAdhaFest',
    component: () => import('@/pages/activity/persian-kol2/index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'default',
      backPath: ['/login', '/register'],
      props: { header: { border: false }, footer: { margin: false } },
      title: '',
      description: t(''),
      mobilePath: 'h5/activity/EidalAdhaFest'
    }
  },
  {
    path: '/h5/activity/EidalAdhaFest',
    component: () => import('@/pages/activity/persian-kol2/index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'activityH5',
      props: { header: { border: false }, footer: { margin: false } },
      title: '',
      pcPath: 'activity/EidalAdhaFest'
    }
  }, {
    path: '/activity/TIFT2025',
    component: () => import('@/pages/activity/platform-team/index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'default',
      backPath: ['/login', '/register'],
      props: { header: { border: false }, footer: { margin: false } },
      title: t('Crypto Trading | Futures Trading | Futures Tournament | Toobit '),
      description: t('Join crypto trading tournament to earn massive rewards, a share of 3 million USDT prize pool. Sign up on Toobit and join the futures trading tournament. '),
      mobilePath: 'h5/activity/TIFT2025'
    }
  },
  {
    path: '/h5/activity/TIFT2025',
    component: () => import('@/pages/activity/platform-team/index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'activityH5',
      props: { header: { border: false }, footer: { margin: false } },
      title: '',
      pcPath: 'activity/TIFT2025'
    }
  },
  {
    path: '/activity/commissionboost',
    component: () => import('@/pages/activity/persian-anniversary1/Index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'default',
      backPath: ['/login', '/register'],
      props: { header: { border: false }, footer: { margin: false } },
      title: '',
      description: t(''),
      mobilePath: 'h5/activity/commissionboost'
    }
  },

  {
    path: '/h5/activity/commissionboost',
    component: () => import('@/pages/activity/persian-anniversary1/Index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'activityH5',
      props: { header: { border: false }, footer: { margin: false } },
      title: '',
      pcPath: 'activity/commissionboost'
    }
  },
  {
    path: '/h5/activity/squad-showdown-competition',
    component: () => import('@/pages/activity/persian-kol/h5/Index.vue'),
    meta: {
      layout: 'h5',
      theme: 'dark',
      isStay: true,
      title: 'Persian activity'
    }
  },
  {
    path: '/ambassador-recruit',
    component: () => import('@/pages/activity/recruit/index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'default',
      backPath: ['/login', '/register'],
      props: { header: { border: false }, footer: { margin: false } },
      title: t('Toobit 全球大使计划'),
      mobilePath: 'h5/ambassador-recruit'
    }
  },
  {
    path: '/h5/ambassador-recruit',
    component: () => import('@/pages/activity/recruit/index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'activityH5',
      backPath: ['/login', '/register'],
      props: { header: { border: false }, footer: { margin: false } },
      title: t('Toobit 全球大使计划'),
      pcPath: 'ambassador-recruit'
    }
  },
  {
    path: '/activity/pixel',
    component: () => import('@/pages/activity/Pixel/Index.vue'),
    meta: {
      layout: 'blank',
      class: {
        layout: 'h5-pixel',
        header: ''
      }
    }
  },
  {
    path: '/h5/activity/',
    component: () => import('@/pages/activity/h5/Index.vue'),
    meta: {

      layout: '',
      // auth: 'login',
      title: t('活动')
    },
    redirect: '/h5/activity/user-tasks',
    children: [
      {
        path: 'user-tasks',
        component: () => import('@/pages/activity/h5/NewUserTasks.vue'),
        meta: {
          class: {
            layout: 'NewUserTasks-box',
            header: 'NewUserTasks-header'
          },
          // auth: 'login',
          props: { header: { border: false } },
          title: t('新手礼包')
        }
      },
      {
        path: 'korea-tasks',
        component: () => import('@/pages/activity/h5/KoreaUserTasks.vue'),
        meta: {
          class: {
            layout: 'NewUserTasks-box',
            header: 'NewUserTasks-header'
          },
          layout: 'blank',
          // auth: 'login',
          props: { header: { border: false } },
          title: t('新手礼包')
        }
      }
    ]
  },
  {
    path: '/official-verification',
    component: () => import('@/pages/official-verification/Index.vue'),
    meta: {
      title: t('官方验证渠道'),
      description: t('请使用Toobit验证渠道检查来源是否为官方。网站链接、电子邮件地址、Twitter链接或Telegram链接。'),
      layout: 'default',
      props: { header: { border: false } },
      ssg: true
    }
  },
  {
    path: '/h5/official-verification',
    component: () => import('@/pages/official-verification/H5/Index.vue'),
    meta: {
      title: t('官方验证渠道'),
      description: t('请使用Toobit验证渠道检查来源是否为官方。网站链接、电子邮件地址、Twitter链接或Telegram链接。'),
      layout: 'h5',
      theme: 'dark'
      // ssg: true
    }
  },
  {
    path: '/copytrading/activity',
    component: () => import('@/pages/copy-trading/activity/Index.vue'),
    meta: {
      title: t('成为交易员'),
      layout: 'default',
      props: { header: { border: false, theme: 'dark' } }
    }
  },
  {
    path: '/h5/copytrading/activity',
    component: () => import('@/pages/copy-trading/activity/h5/Index.vue'),
    meta: {
      layout: 'blank',
      auth: 'ignore',
      title: t('成为交易员')
    }
  },
  {
    path: '/card-deposit',
    component: () => import('@/pages/card-deposit/Index.vue'),
    beforeEnter: forwardToExternal,
    meta: {
      theme: true,
      external: true
    }
  },
  {
    path: '/h5/fiat-submit',
    component: () => import('@/pages/card-deposit/h5/Form.vue'),
    meta: {
      title: 'Fiat deposit ｜buy Crypto via Express Checkout',
      keywords: 'buy crypto, buy btc, buy usdt, buy eth, visa,  credit card',
      description: 'Buy BTC, USDT, ETH and other cryptocurrencies in three easy steps using multiple methods of payment.',
      layout: ''
    }
  },
  {
    path: '/fiat-order',
    component: () => import('@/pages/fiat-order/Index.vue'),
    beforeEnter: forwardToExternal,
    meta: {
      theme: true,
      external: true
    }
  },
  {
    path: '/notifications',
    component: () => import('@/pages/notifications/Index.vue'),
    meta: {
      layout: 'default',
      title: t('通知'),
      auth: 'login',
      class: {
        layout: 'notifications-box',
        header: ''
      },
      props: { header: { border: false } }
    }
  },
  {
    path: '/lite-perpetual',
    component: () => import('@/pages/lite-perpetual/Index.vue'),
    meta: {
      layout: 'exchange',
      theme: true,
      gridlayout: 'lite',
      class: {
        header: 'base-wrapper',
        layout: 'responsive-layout overflow-hidden'
      },
      props: { header: { border: false, headerSetting: true } },
      title: t('Toobit｜USDT 永续合约'),
      keywords: t('合约交易，比特币交易，比特币合约，BTC价格'),
      description: t('在Toobit Futures、简易合约上交易BTCUSDT永久合约。'),
      ssg: true,
      pageId: 4
    }
  },
  {
    path: '/lite-perpetual/:symbolPath',
    component: () => import('@/pages/lite-perpetual/Index.vue'),
    meta: {
      layout: 'exchange',
      theme: true,
      gridlayout: 'lite',
      class: {
        header: 'base-wrapper',
        layout: 'responsive-layout overflow-hidden'
      },
      props: { header: { border: false, headerSetting: true } },
      title: t('Toobit｜USDT 永续合约'),
      keywords: t('合约交易，比特币交易，比特币合约，BTC价格'),
      description: t('在Toobit Futures、简易合约上交易BTCUSDT永久合约。'),
      ssg: true,
      pageId: 4
    }
  },
  {
    path: '/demo-futures',
    component: () => import('@/pages/demo-futures/Index.vue'),
    meta: {
      layout: 'exchange',
      theme: true,
      gridlayout: 'demo-futures',
      class: {
        header: 'base-wrapper',
        layout: 'responsive-layout overflow-hidden'
      },
      props: { header: { border: false, headerSetting: true } },
      title: t('Toobit｜USDT 永续合约'),
      keywords: t('合约交易，比特币交易，比特币合约，BTC价格'),
      description: t('在Toobit Futures、简易合约上交易BTCUSDT永久合约。'),
      ssg: true,
      pageId: 10
    }
  },
  {
    path: '/demo-futures/:symbolPath',
    component: () => import('@/pages/demo-futures/Index.vue'),
    meta: {
      layout: 'exchange',
      theme: true,
      gridlayout: 'demo-futures',
      class: {
        header: 'base-wrapper',
        layout: 'responsive-layout overflow-hidden'
      },
      props: { header: { border: false, headerSetting: true } },
      title: t('Toobit｜USDT 永续合约'),
      keywords: t('合约交易，比特币交易，比特币合约，BTC价格'),
      description: t('在Toobit Futures、简易合约上交易BTCUSDT永久合约。'),
      ssg: true,
      pageId: 10
    }
  },
  {
    path: '/sub-orders/',
    component: () => import('@/pages/sub-order/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('子账户订单管理')
    },
    children: [
      {
        path: 'spot/:id',
        component: () => import('@/pages/order/Index.vue'),
        meta: {
          orderType: 'spot',
          isSubOrder: true
        }
      },
      {
        path: 'futures/:id',
        component: () => import('@/pages/futures-order/Index.vue'),
        meta: {
          orderType: 'futures',
          isSubOrder: true
        }
      }
    ]
  },
  {
    path: '/sub-assets-transfer',
    component: () => import('@/pages/sub-assets-transfer/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('划转记录'),
      isSubOrder: true
    }
    //
  },
  {
    path: '/community',
    component: () => import('@/pages/community/Index.vue'),
    meta: {
      layout: 'default',
      theme: 'dark',
      class: {
        header: 'base-wrapper',
        footer: 'community-footer new-color-footer',
        layout: 'base-wrapper'
      },
      title: t('Toobit 社区'),
      props: {
        header: { border: false },
        footer: { theme: 'dark', margin: false }
      },
      h5: 'community'
    }
  },
  {
    path: '/futures-strategy',
    component: () => import('@/pages/futures-strategy/exchange/Index.vue'),
    meta: {
      layout: 'exchange',
      theme: 'dark',
      title: t('期货网格交易｜加密货币期货交易机器人｜Martingale交易交易机器人｜Toobit'),
      description: t('使用我们的期货网格交易机器人或Martingale交易机器人以预定的价格水平自动买卖BTC，ETH，SOL，BNB，XRP，DOGE，SHIB等等。利用Toobit加密期货交易机器人享受加密世界的最佳利润。'),
      gridlayout: 'futures-strategy',
      pageId: 9,
      props: {
        header: { border: false }
      }
    }
  },
  {
    path: '/futures-strategy/:symbolPath',
    component: () => import('@/pages/futures-strategy/exchange/Index.vue'),
    meta: {
      layout: 'exchange',
      theme: 'dark',
      title: t('期货网格交易｜加密货币期货交易机器人｜Martingale交易交易机器人｜Toobit'),
      description: t('使用我们的期货网格交易机器人或Martingale交易机器人以预定的价格水平自动买卖BTC，ETH，SOL，BNB，XRP，DOGE，SHIB等等。利用Toobit加密期货交易机器人享受加密世界的最佳利润。'),
      gridlayout: 'futures-strategy',
      im: true,
      pageId: 9,
      props: {
        header: { border: false }
      }
    }
  },
  {
    path: '/strategy-detail/:id',
    component: () => import('@/pages/futures-strategy/detail/Index.vue'),
    meta: {
      layout: 'default',
      theme: 'dark',
      title: t('BTC & ETH合约交易｜比特币合约交易所｜合约网格交易｜合约Martingale交易｜Toobit'),
      description: t('查看您的BTC、ETH、SOL、BNB、XRP、DOGE、SHIB等代币交易历史和即时利润。取得最新的BTC、ETH、SOL、BNB、XRP、DOGE、SHIB等币种交易数据。加入加密货币交易所Toobit，透过期货网格交易机器人交易比特币USDT。'),
      pageId: 9,
      props: {
        header: { border: false }
      }
    }
  },
  {
    path: '/strategy-detail/:id/:type',
    component: () => import('@/pages/futures-strategy/martingaleDetail/Index.vue'),
    meta: {
      layout: 'default',
      theme: 'dark',
      title: t('BTC & ETH合约交易｜比特币合约交易所｜合约网格交易｜合约Martingale交易｜Toobit'),
      description: t('查看您的BTC、ETH、SOL、BNB、XRP、DOGE、SHIB等代币交易历史和即时利润。取得最新的BTC、ETH、SOL、BNB、XRP、DOGE、SHIB等币种交易数据。加入加密货币交易所Toobit，透过期货网格交易机器人交易比特币USDT。'),
      pageId: 9,
      props: {
        header: { border: false }
      }
    }
  },
  {
    path: '/trading-bot',
    component: () => import('@/pages/futures-strategy/bot/Index.vue'),
    meta: {
      layout: 'default',
      title: t('交易机器人｜加密货币交易机器人｜合约网格与期货DCA｜Toobit'),
      description: t('查看Toobit自动加密货币交易机器人，其中包括合约网格机器人和合约马丁格尔机器人。在波动的加密货币市场中最大化您的利润并最小化您的风险。'),
      im: true,
      pageId: 9,
      props: {
        header: { border: false }
      }
    }
  },
  {
    path: '/share',
    component: () => import('@/pages/share/SocialMedia.vue'),
    meta: {
      layout: 'blank',
      title: t('Toobit - 加密货币交易平台｜交易比特币、以太坊的加密货币交易所'),
      keywords: t('加密货币交易所 ｜数字货币交易平台 ｜比特币交易 ｜Toobit'),
      description: t('Toobit是一家领先的加密货币交易所，提供比特币、以太坊和各种加密货币的现货、合约和跟单交易服务。'),
      props: {
        header: { border: false }
      },
      ssg: true,
      tags: [
        { name: 'og:url', content: 'https://www.toobit.com' },
        { name: 'og:image', content: 'https://www.toobit.com/share/share-24-06.jpeg' },
        { name: 'og:title', content: t('Toobit - 加密货币交易平台｜交易比特币、以太坊的加密货币交易所') },
        { name: 'og:description', content: t('Toobit是一家领先的加密货币交易所，提供比特币、以太坊和各种加密货币的现货、合约和跟单交易服务。') },
        { name: 'og:image:secure_url', content: 'https://www.toobit.com/share/share-24-06.jpeg' },
        { name: 'og:image:width', content: '1200' },
        { name: 'og:image:height', content: '630' },
        { name: 'og:image:alt', content: t('Toobit - 加密货币交易平台｜交易比特币、以太坊的加密货币交易所') },
        { name: 'linkedin:image', content: 'https://www.toobit.com/share/share-24-06.jpeg' },
        { name: 'linkedin:title', content: t('Toobit - 加密货币交易平台｜交易比特币、以太坊的加密货币交易所') },
        { name: 'linkedin:description', content: t('Toobit是一家领先的加密货币交易所，提供比特币、以太坊和各种加密货币的现货、合约和跟单交易服务。') },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:image', content: 'https://www.toobit.com/share/share-24-06.jpeg' },
        { name: 'twitter:image:src', content: 'https://www.toobit.com/share/share-24-06.jpeg' },
        { name: 'twitter:title', content: t('Toobit - 加密货币交易平台｜交易比特币、以太坊的加密货币交易所') },
        { name: 'twitter:description', content: t('Toobit是一家领先的加密货币交易所，提供比特币、以太坊和各种加密货币的现货、合约和跟单交易服务。') }
      ]
    }
  },
  {
    path: '/activity/t/:id',
    component: () => import('@/pages/activity/template/Index.vue'),
    meta: {
      layout: 'default',
      theme: 'dark',
      class: {
        header: 'base-wrapper',
        footer: 'base-wrapper',
        layout: 'base-wrapper'
      },
      title: t('活动'),
      backPath: ['/login', '/register'],
      isStay: true,
      props: {
        header: { border: false },
        footer: { theme: 'dark', margin: false }
      },
      mobilePath: 'h5/activity/t/{id}'
    }
  },
  {
    path: '/activity/c/:id',
    component: () => import('@/pages/activity/template/Index.vue'),
    meta: {
      customize: true, // 自定义活动链接
      layout: 'default',
      theme: 'dark',
      class: {
        header: 'base-wrapper',
        footer: 'base-wrapper',
        layout: 'base-wrapper'
      },
      title: t('活动'),
      backPath: ['/login', '/register'],
      isStay: true,
      props: {
        header: { border: false },
        footer: { theme: 'dark', margin: false }
      },
      mobilePath: 'h5/activity/c/{id}'
    }
  },
  {
    path: '/h5/activity/t/:id',
    component: () => import('@/pages/activity/template/Index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'activityH5',
      props: { header: { border: false }, footer: { margin: false } },
      title: t('活动'),
      pcPath: 'activity/t/{id}'
    }
  },
  {
    path: '/h5/activity/c/:id',
    component: () => import('@/pages/activity/template/Index.vue'),
    meta: {
      customize: true, // 自定义活动链接
      isStay: true,
      theme: 'dark',
      layout: 'activityH5',
      props: { header: { border: false }, footer: { margin: false } },
      title: t('活动'),
      pcPath: 'activity/c/{id}'
    }
  },
  {
    path: '/activity/easter',
    component: () => import('@/pages/activity/easter/Index.vue'),
    meta: {
      layout: 'default',
      theme: 'dark',
      class: {
        header: 'base-wrapper',
        footer: 'base-wrapper',
        layout: 'base-wrapper'
      },
      title: t('Toobit复活节彩蛋狂欢'),
      backPath: ['/login', '/register'],
      isStay: true,
      props: {
        header: { border: false },
        footer: { theme: 'dark', margin: false }
      },
      mobilePath: 'h5/activity/easter'
    }
  },
  {
    path: '/h5/activity/easter',
    component: () => import('@/pages/activity/easter/Index.vue'),
    meta: {
      isStay: true,
      theme: 'dark',
      layout: 'activityH5',
      props: { header: { border: false }, footer: { margin: false } },
      title: t('Toobit复活节彩蛋狂欢'),
      pcPath: 'activity/easter'
    }
  },
  {
    path: '/earn',
    component: () => import('@/pages/earn/Index.vue'),
    meta: {
      title: t('Toobit｜理财'),
      keywords: t('Toobit｜理财'),
      description: t('Toobit｜理财'),
      isStay: true,
      class: {
        layout: 'header-transparent'
      },
      layout: 'default',
      auth: '',
      props: { header: { border: false } }
    }
  },
  {
    path: '/network-monitoring',
    component: () => import('@/pages/network-monitoring/Index.vue'),
    meta: {
      title: t('Toobit｜网络监测'),
      layout: 'default',
      isStay: true,
      auth: ''
    }
  },
  {
    path: '/insights',
    component: () => import('@/pages/insights/Index.vue'),
    meta: {
      title: t('Toobit｜跟单广场'),
      keywords: t('跟单交易，加密货币跟单交易，数字货币跟单交易'),
      description: t('在toobit.com发现并跟随顶级交易员'),
      class: {
        layout: 'header-transparent'
      },
      layout: 'default',
      auth: import.meta.env.MODE !== 'development' ? 'login' : undefined,
      im: true,
      props: { header: { border: false } },
      ssg: true
    }
  },
  {
    path: '/insights/settings',
    component: () => import('@/pages/insights/settings/Index.vue'),
    meta: {
      title: t('Toobit｜跟单广场'),
      keywords: t('跟单交易，加密货币跟单交易，数字货币跟单交易'),
      description: t('在toobit.com发现并跟随顶级交易员'),
      class: {
        layout: 'header-transparent'
      },
      layout: 'default',
      props: { header: { border: false } },
      ssg: false
    }
  },
  {
    path: '/insights/settings/white',
    component: () => import('@/pages/insights/settings/WhiteList.vue'),
    meta: {
      title: t('Toobit｜跟单广场'),
      keywords: t('跟单交易，加密货币跟单交易，数字货币跟单交易'),
      description: t('在toobit.com发现并跟随顶级交易员'),
      class: {
        layout: 'header-transparent'
      },
      layout: 'default',
      props: { header: { border: false } },
      ssg: false
    }
  },
  {
    path: '/insights/subscribe',
    component: () => import('@/pages/insights/subscribe/Index.vue'),
    meta: {
      title: t('Toobit｜跟单广场'),
      keywords: t('跟单交易，加密货币跟单交易，数字货币跟单交易'),
      description: t('在toobit.com发现并跟随顶级交易员'),
      class: {
        layout: 'header-transparent'
      },
      layout: 'default',
      props: { header: { border: false } },
      ssg: false
    }
  },
  {
    path: '/insights/posts/:id',
    component: () => import('@/pages/insights/Posts.vue'),
    meta: {
      title: t('Toobit｜观点详情'),
      class: {
        layout: 'header-transparent'
      },
      layout: 'default',
      props: { header: { border: false } },
      ssg: true
    }
  },
  {
    path: '/insights/topics/:id',
    component: () => import('@/pages/insights/Topics.vue'),
    meta: {
      title: t('Toobit｜话题详情'),
      class: {
        layout: 'header-transparent'
      },
      layout: 'default',
      props: { header: { border: false } },
      ssg: false
    }
  },
  {
    path: '/signalclone',
    component: () => import('@/pages/insights/signalclone.vue'),
    meta: {
      title: t('Toobit - 加密货币交易平台｜交易比特币、以太坊的加密货币交易所'),
      keywords: t('加密货币交易所 ｜数字货币交易平台 ｜比特币交易 ｜Toobit'),
      description: t('Toobit是一家领先的加密货币交易所，提供比特币、以太坊和各种加密货币的现货、合约和跟单交易服务。'),
      layout: 'blank',
      ssg: true
    }
  },

  {
    path: '/academy',
    component: () => import('@/pages/novice-academy/index.vue'),
    meta: {
      layout: 'default',
      title: t('学习比特币、加密货币、交易、NFTs 和 Web3 | Toobit 学院'),
      description: t('在 Toobit 以有趣的方式获得加密货币知识！透过 Toobit 学院学习，我们涵盖的主题包括比特币、以太坊、加密货币交易、市场分析、区块链等等。'),
      props: { header: { border: false }, footer: { margin: false } }
    }
  },
  {
    path: '/academy/search',
    component: () => import('@/pages/novice-academy/search.vue'),
    meta: {
      layout: 'default',
      title: t('学习比特币、加密货币、交易、NFTs 和 Web3 | Toobit 学院'),
      description: t('在 Toobit 以有趣的方式获得加密货币知识！透过 Toobit 学院学习，我们涵盖的主题包括比特币、以太坊、加密货币交易、市场分析、区块链等等。'),
      props: {
        header: { border: false },
        footer: { margin: false }
      }
    }
  },

  {
    path: '/academy/category',
    component: () => import('@/pages/novice-academy/category.vue'),
    meta: {
      layout: 'default',
      title: t('学习比特币、加密货币、交易、NFTs 和 Web3 | Toobit 学院'),
      description: t('在 Toobit 以有趣的方式获得加密货币知识！透过 Toobit 学院学习，我们涵盖的主题包括比特币、以太坊、加密货币交易、市场分析、区块链等等。'),
      props: {
        header: { border: false }
      }
    }
  },
  {
    path: '/academy/article/detail/:id',
    // name: 'article-detail',
    component: () => import('@/pages/novice-academy/article-detail.vue'),
    meta: {
      layout: 'default',
      title: t('学习比特币、加密货币、交易、NFTs 和 Web3 | Toobit 学院'),
      description: t('在 Toobit 以有趣的方式获得加密货币知识！透过 Toobit 学院学习，我们涵盖的主题包括比特币、以太坊、加密货币交易、市场分析、区块链等等。'),
      props: {
        header: { border: false }
      }
    }
  },
  {
    path: '/h5/academy',
    component: () => import('@/pages/h5-learn/Index.vue'),
    meta: {
      layout: 'h5',
      auth: 'ignore',
      title: t('学习比特币、加密货币、交易、NFTs 和 Web3 | Toobit 学院'),
      description: t('在 Toobit 以有趣的方式获得加密货币知识！透过 Toobit 学院学习，我们涵盖的主题包括比特币、以太坊、加密货币交易、市场分析、区块链等等。')
    }
  },
  {
    path: '/h5/academy/search',
    component: () => import('@/pages/h5-learn/Search.vue'),
    meta: {
      layout: 'h5',
      auth: 'ignore',
      title: t('学习比特币、加密货币、交易、NFTs 和 Web3 | Toobit 学院'),
      description: t('在 Toobit 以有趣的方式获得加密货币知识！透过 Toobit 学院学习，我们涵盖的主题包括比特币、以太坊、加密货币交易、市场分析、区块链等等。')
    }
  },
  {
    path: '/h5/academy/category',
    component: () => import('@/pages/h5-learn/Category.vue'),
    meta: {
      layout: 'h5',
      auth: 'ignore',
      title: t('学习比特币、加密货币、交易、NFTs 和 Web3 | Toobit 学院'),
      description: t('在 Toobit 以有趣的方式获得加密货币知识！透过 Toobit 学院学习，我们涵盖的主题包括比特币、以太坊、加密货币交易、市场分析、区块链等等。')
    }
  },
  {
    path: '/h5/academy/article/detail/:id',
    // name: 'h5-article-detail',
    component: () => import('@/pages/h5-learn/ArticleDetail.vue'),
    meta: {
      layout: 'h5',
      auth: 'ignore',
      title: t('学习比特币、加密货币、交易、NFTs 和 Web3 | Toobit 学院'),
      description: t('在 Toobit 以有趣的方式获得加密货币知识！透过 Toobit 学院学习，我们涵盖的主题包括比特币、以太坊、加密货币交易、市场分析、区块链等等。')
    }
  },

  {
    path: '/learn',
    redirect: '/academy'
  },
  {
    path: '/learn/search',
    redirect: '/academy/search'
  },
  {
    path: '/learn/category',
    redirect: '/academy/category'
  },
  {
    path: '/learn/article/detail/:id',
    redirect: (to: any) => {
      return { path: `/academy/article/detail/${to.params.id}` };
    }
  },
  {
    path: '/h5/learn',
    redirect: '/h5/academy'
  },
  {
    path: '/h5/learn/search',
    redirect: '/h5/academy/search'
  },
  {
    path: '/h5/learn/category',
    redirect: '/h5/academy/category'
  },
  {
    path: '/h5/learn/article/detail/:id',
    redirect: to => {
      return { path: `/h5/learn/article/detail/${to.params.id}` };
    }
  },
  {
    path: '/recharge-refund',
    component: () => import('@/pages/recharge-refund/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('充值未到账找回')
    }
  },
  {
    path: '/recharge-refund/apply-form',
    component: () => import('@/pages/recharge-refund/RefundForm.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('充值未到账找回')
    }
  },
  {
    path: '/h5/recharge-refund/apply-form',
    component: () => import('@/pages/recharge-refund/h5/ApplyFormH5.vue'),
    meta: {
      layout: 'h5',
      title: t('充值未到账找回')
    }
  },
  {
    path: '/h5/recharge-refund',
    component: () => import('@/pages/recharge-refund/h5/Refund.vue'),
    meta: {
      layout: 'h5',
      title: t('充值未到账找回')
    }
  },
  {
    path: '/user/persia-kyc',
    component: () => import('@/pages/user/persia-kyc/Index.vue'),
    meta: {
      layout: 'default',
      auth: 'login'
    }
  },
  {
    path: '/safety',
    component: () => import('@/pages/safety/index.vue'),
    meta: {
      layout: 'default',
      theme: 'dark',
      props: { footer: { margin: false } },
      title: t('Toobit安全方案'),
      keywords: t('Toobit安全：您值得信赖的交易平台'),
      description: t('在Toobit，我们通过集成多层加密、AI驱动的威胁检测和高级多重身份验证(MFA)来全天候保护用户资产，以维持最高的安全标准。'),
      ssg: true
    }
  },
  {
    path: '/user-exp',
    component: () => import('@/pages/user-execption/index.vue'),
    meta: {
      layout: 'default',
      auth: '',
      backPath: ['/login'],
      props: { header: { border: false } }
    }
  },
  {
    path: '/h5/user-exp',
    component: () => import('@/pages/user-execption/index.vue'),
    meta: {
      layout: 'h5',
      auth: '',
      backPath: ['/login'],
      props: { header: { border: false } }
    }
  },
  {
    path: '/islamic-account',
    component: () => import('@/pages/islamic-account/Index.vue'),
    meta: {
      layout: 'default',
      auth: '',
      backPath: ['/login'],
      props: { header: { border: false }, footer: { margin: false } }
    }
  },
  {
    path: '/convert',
    component: () => import('@/pages/convert/Index.vue'),
    meta: {
      layout: 'default',
      auth: '',
      isStay: true,
      title: t('闪兑'),
      description: t('闪兑'),
      theme: 'dark',
      props: { header: { border: false }, footer: { margin: false } }
    }
  },
  {
    path: '/convert/history',
    component: () => import('@/pages/convert/History.vue'),
    meta: {
      layout: 'default',
      auth: 'login',
      title: t('闪兑交易记录'),
      description: t('闪兑交易记录'),
      props: { header: { border: false }, footer: { margin: false } }
    }
  },
  {
    path: '/h5/recaptcha',
    component: () => import('@/pages/recaptcha/Index.vue'),
    meta: {
      layout: 'blank',
      title: 'reCAPTCHA'
    }
  },
  {
    path: '/novameme',
    component: () => import('@/pages/nova/Index.vue'),
    meta: {
      title: 'Toobit x Nova.Meme',
      layout: 'default',
      theme: 'dark',
      isStay: true,
      props: { header: { border: false }, footer: { margin: false } }
    }
  },
  {
    path: '/theascent',
    component: () => import('@/pages/spokesperson/Index.vue'),
    meta: {
      layout: 'webNew',
      props: { footer: { margin: false } },
      class: { layout: 'dark' },
      theme: 'dark',
      title: t('一种不同的可扩展性 — Toobit x Chris sharma'),
      isStay: true,
      mobilePath: 'h5/theascent'
    }
  },
  {
    path: '/h5/theascent',
    component: () => import('@/pages/spokesperson/h5.vue'),
    meta: {
      theme: 'dark',
      layout: 'h5',
      title: t('一种不同的可扩展性 — Toobit x Chris sharma'),
      isStay: true,
      pcPath: 'theascent'
    }
  },
  {
    path: '/forex',
    beforeEnter: forwardToExternal,
    meta: {
      theme: 'dark',
      external: true
    }
  },
  {
    path: '/h5/forex',
    beforeEnter: forwardToExternal,
    meta: {
      theme: 'dark',
      external: true
    }
  },
  {
    path: '/spokesperson',
    redirect: '/theascent'
  },
  {
    path: '/h5/spokesperson',
    redirect: '/h5/theascent'
  },
  {
    path: '/dex/:symbolPath',
    beforeEnter: forwardToExternal,
    meta: {
      external: true
    }
  },
  {
    path: '/dex',
    beforeEnter: forwardToExternal,
    meta: {
      external: true
    }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
];

if (import.meta.env !== 'production') {
  routes = routes.concat([
    {
      path: '/dev/grid',
      component: () => import('@/components/layout/GridLayout.vue'),
      meta: {
        layout: 'exchange'
      }
    },
    {
      path: '/dev/system/icons',
      component: () => import('@/pages/custom/system/icons.vue'),
      meta: {
        layout: 'default',
        title: t('学习比特币、加密货币、交易、NFTs 和 Web3 | Toobit 学院'),
        description: t('在 Toobit 以有趣的方式获得加密货币知识！透过 Toobit 学院学习，我们涵盖的主题包括比特币、以太坊、加密货币交易、市场分析、区块链等等。'),
        props: {
          header: { border: false }
        }
      }
    }
  ]);
}

routes.push({
  path: '/:pathMatch(.*)*',
  redirect: '/404'
});

export default routesHandle(routes);
